import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../Game.css';
import './GenderEqualityCuisine.css';
import api from '../../utils/api';
import { STORAGE_KEYS } from '../../constants';
import WaitingStage from './WaitingStage';
import VotingStage from './VotingStage';
import RevealStage from './RevealStage';

export interface GECRoomStatus {
  gameState: 'preparation' | 'ongoing' | 'ended';
  chefCount: number;
  voterCount: number;
  totalParticipants: number;
  dishesSubmitted: number;
  totalChefs: number;
  votedCount: number;
  totalVoters: number;
  currentUserRole: 'chef' | 'voter' | null;
  currentUserDishSubmitted: boolean;
  currentUserVoted: boolean;
}

interface Props {
  roomId: string;
  roomData: any;
  isAdmin: boolean;
}

function GenderEqualityCuisine({ roomId, roomData, isAdmin }: Props) {
  const [status, setStatus] = useState<GECRoomStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [endingGame, setEndingGame] = useState(false);
  const stageFromRoom = (roomData?.gameState as GECRoomStatus['gameState']) || 'preparation';
  const myUserIdRef = useRef<string>('');

  useEffect(() => {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (userData) {
      const u = JSON.parse(userData);
      myUserIdRef.current = u.userId || u.user_id || '';
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    const result = await api.gecGetRoomStatus(roomId);
    if (result.success) {
      setStatus(result.data);
    }
    setStatusLoading(false);
  }, [roomId]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleEndGame = async () => {
    if (!window.confirm('End voting and reveal the results?')) return;
    setEndingGame(true);
    const result = await api.endGame(roomId);
    setEndingGame(false);
    if (result.success) {
      fetchStatus();
      window.location.reload(); // ensure roomData (in GameRoom) refetches as well
    } else {
      alert(result.error);
    }
  };

  if (statusLoading || !status) {
    return (
      <div className="game-container">
        <div className="game-content">
          <div className="loading">🍳 Loading kitchen…</div>
        </div>
      </div>
    );
  }

  const effectiveStage = status.gameState || stageFromRoom;

  return (
    <div className="game-container gec">
      <div className="game-header gec-header">
        <h2>👩‍🍳 Gender Equality Cuisine</h2>
        <div className="room-id-display">{roomId}</div>
        <div className="game-info">
          <div className="info-item">
            <div className="info-label">Room</div>
            <div className="info-value">{roomData?.roomName}</div>
          </div>
          <div className="info-item">
            <div className="info-label">🧑‍🍳 Chefs</div>
            <div className="info-value">{status.chefCount}</div>
          </div>
          <div className="info-item">
            <div className="info-label">🗳️ Voters</div>
            <div className="info-value">{status.voterCount}</div>
          </div>
        </div>
      </div>

      {effectiveStage === 'preparation' && (
        <WaitingStage
          roomId={roomId}
          status={status}
          isAdmin={isAdmin}
          refreshStatus={fetchStatus}
        />
      )}

      {effectiveStage === 'ongoing' && (
        <VotingStage
          roomId={roomId}
          status={status}
          isAdmin={isAdmin}
          myUserId={myUserIdRef.current}
          onEndGame={handleEndGame}
          endingGame={endingGame}
          refreshStatus={fetchStatus}
        />
      )}

      {effectiveStage === 'ended' && (
        <RevealStage roomId={roomId} />
      )}
    </div>
  );
}

export default GenderEqualityCuisine;
